import { useProductPageStore } from '../../contexts/ProductPageStore'
import styles from './asideRecommendations.module.scss'

import React, { useContext, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'

import { useUserStore } from 'Clutch/Stores/UserStore/UserStore'
import ErrorBoundary from 'Clutch/UtilityComponents/ErrorBoundary'
import Image from 'next/image'

import { getSuperMarketAffinity } from 'Clutch/Utilities/Instrumentation/SegmentUtilities/segmentFunctions'

import { TrackingContext } from 'Clutch/Contexts/TrackingContext/TrackingContext'

import useRecentHistory from 'Clutch/Hooks/useRecentHistory'

import AddToCart from 'Clutch/Organisms/EnhancedProductCard/Shared/AddToCart'

import Card from 'Clutch/Atoms/Card'
import Divider from 'Clutch/Atoms/Divider'
import Rating from 'Clutch/Atoms/Rating'
import Ribbon from 'Clutch/Atoms/Ribbon'
import TextLink from 'Clutch/Atoms/TextLink'
import Typography from 'Clutch/Atoms/Typography'

const SpeedwayAlternateRecommendations = ({}) => {
  const speedwayAlternativeProducts = useProductPageStore((x) => x.speedwayAlternativeProducts)

  const { segmentReady } = useContext(TrackingContext)
  const userVin = useUserStore((x) => x.context.userVin)
  const userGuid = useUserStore((x) => x.context.userGuid)

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
    rootMargin: '0px 0px', //because this doesn't initiate a request
  })

  const title = 'Same Performance, Lower Price'

  const listName = 'pdp_speedway_alts'

  useEffect(() => {
    if (inView) {
      //send segment product list viewed
      //this feeds miso recommendation impressions
      if (segmentReady && (speedwayAlternativeProducts || []).length > 0) {
        analytics.track('Product List Viewed', {
          user_vin: `${userVin()}`,
          web_user_guid: `${userGuid}`,
          list_id: listName,
          category: `${title}`,
          products: (speedwayAlternativeProducts || []).map((alt, i) => {
            return {
              product_id: alt.skuBaseNumber,
              sku: alt.skuBaseNumber,
              name: alt.title,
              price: alt.price,
              position: i,
              category: alt.segmentCategorization,
              url: alt.productPageUrl,
              image_url: alt.imageUrl,
              quantity: 1,
              brand: alt.brand,
              availability: alt.availability,
              rating: alt.reviewCount ? alt.rating : 0,
              reviews: alt.reviewCount,
              audience_affinity: getSuperMarketAffinity(alt),
            }
          }),
        })
      } else {
        console.info('analytics not defined')
      }
    }
  }, [inView, segmentReady])

  if (!(speedwayAlternativeProducts || []).length) return false

  return (
    <ErrorBoundary>
      <div id={'pdp_speedway_alternates'} className={styles.pdp_aside_cards} ref={ref}>
        <Card fill>
          <Typography className={styles.header} size={1.25} as={'h2'} font={'heading'}>
            {title}
          </Typography>
          <Typography size={0.875}>{`Quality parts from Speedway Motors at competitive prices.`}</Typography>
          <Divider className={styles.divider} />
          <div className={styles.alts_spacing}>
            {speedwayAlternativeProducts.map((speedwayAlternativeProduct, index) => {
              const product = {
                skuBaseNumber: speedwayAlternativeProduct.skuBaseNumber,
                brand: speedwayAlternativeProduct.brand,
                reviewCount: speedwayAlternativeProduct.reviewCount,
                rating: speedwayAlternativeProduct.rating,
                displayTitle: speedwayAlternativeProduct.title,
                segmentCategorization: speedwayAlternativeProduct.segmentCategorization,
                salePrice: speedwayAlternativeProduct.price,
                productPageUrl: speedwayAlternativeProduct.productPageUrl,
                thumbnailImageLink: speedwayAlternativeProduct.imageUrl,
              }

              const clickEvent = {
                event: 'Product Clicked',
                properties: {
                  list_id: listName,
                  product_id: speedwayAlternativeProduct.skuBaseNumber,
                  sku: speedwayAlternativeProduct.skuBaseNumber,
                  category: speedwayAlternativeProduct.segmentCategorization,
                  name: speedwayAlternativeProduct.title,
                  brand: speedwayAlternativeProduct.brand,
                  variant: speedwayAlternativeProduct?.skuVariantNumber ?? null,
                  price: speedwayAlternativeProduct.price,
                  url: speedwayAlternativeProduct.productPageUrl,
                  image_url: speedwayAlternativeProduct.imageUrl,
                  availability: speedwayAlternativeProduct.availability,
                  rating: speedwayAlternativeProduct.reviewCount ? speedwayAlternativeProduct.rating : 0,
                  reviews: speedwayAlternativeProduct.reviewCount,
                  audience_affinity: getSuperMarketAffinity(speedwayAlternativeProduct),
                },
              }
              return (
                <div className={styles.rec_wrapper} key={speedwayAlternativeProduct.skuBaseNumber || index}>
                  <Ribbon
                    text={`Save ${speedwayAlternativeProduct.priceOffString}`}
                    brand={'secondary'}
                    fontSize={0.75}
                  />
                  <TextLink
                    as={'div'}
                    className={styles.rec_image_wrapper}
                    href={speedwayAlternativeProduct.productPageUrl}
                    onClick={() => {
                      useRecentHistory.SetMostRecentProductList('rec', listName)
                      try {
                        analytics.track(clickEvent.event, clickEvent.properties)
                      } catch (err) {
                        console.error(`error sending product clicked event`)
                      }
                    }}
                  >
                    <Image
                      src={`https:${speedwayAlternativeProduct.imageUrl ? speedwayAlternativeProduct.imageUrl : '//content.speedwaymotors.com/OtherImages/missingimage2.jpg'}`}
                      alt={speedwayAlternativeProduct?.title || 'Product image'}
                      width={100}
                      height={100}
                    />
                  </TextLink>
                  <span className={styles.alternative_content}>
                    <TextLink
                      size={1}
                      href={speedwayAlternativeProduct.productPageUrl}
                      onClick={() => {
                        useRecentHistory.SetMostRecentProductList('rec', listName)
                      }}
                      segmentEvent={clickEvent}
                    >
                      {speedwayAlternativeProduct.title}
                    </TextLink>
                    <div className={styles.alternative_inner_content}>
                      <div className={styles.alternative_rating_price}>
                        <div className={styles.ratingContainer}>
                          <Rating ratingValue={speedwayAlternativeProduct.rating} size={1} />
                          <Typography size={0.875} tone={'subtle'}>
                            ({speedwayAlternativeProduct.reviewCount})
                          </Typography>
                        </div>
                        <div className={styles.pricing}>
                          <Typography size={1} font={'bold'}>
                            {speedwayAlternativeProduct.priceString}
                          </Typography>
                          <Typography size={1} strikethrough tone={'subtle'}>
                            {speedwayAlternativeProduct.strikeThroughPriceString}
                          </Typography>
                        </div>
                      </div>
                      <div className={styles.alternative_add_to_cart}>
                        <AddToCart
                          product={product}
                          onStartAddToCart={() => {
                            useRecentHistory.SetMostRecentProductList('rec', listName)
                          }}
                        />
                      </div>
                    </div>
                  </span>
                </div>
              )
            })}
          </div>
        </Card>
      </div>
    </ErrorBoundary>
  )
}

export default SpeedwayAlternateRecommendations
